import axios from 'axios';
import progressBar from '../lib/progressBar';
import { clearEventDetailsCache } from '../utils/clearEventDetailsCache';

// Déterminer l'URL de base de l'API avec fallback sécurisé
export const apiBaseUrl = (typeof window !== 'undefined' && window.env && window.env.apiOperational)
  || process.env.REACT_APP_API_OPERATIONAL
  || 'http://localhost:3001';

const api = axios.create({
    baseURL: apiBaseUrl,
});

// --- Auth0 token injection ---
// initApiAuth() is called once from App.js with Auth0's getAccessTokenSilently.
let _getAuthToken = null;
let _auth0Logout = null;
// Plusieurs requêtes en parallèle peuvent recevoir un 401 en même temps : une seule déconnexion.
let _loggingOut = false;

export const initApiAuth = (getTokenFn, logoutFn) => {
  _getAuthToken = getTokenFn;
  _auth0Logout = logoutFn;
  _loggingOut = false;
};

// Token courant pour les clients hors axios (Socket.io, cf. api/socket.js)
export const getApiAuthToken = async () => (_getAuthToken ? _getAuthToken() : null);

api.interceptors.request.use(
  async (config) => {
    // Auth0 token (primary)
    if (_getAuthToken) {
      try {
        const token = await _getAuthToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (err) {
        // Token acquisition failed — la requête part sans en-tête (l'API répondra 401 et la session
        // sera fermée) : on le trace pour ne pas diagnostiquer un 401 « venu de nulle part ».
        console.warn("Impossible d'obtenir le jeton Auth0:", err?.message || err);
      }
    }

    // Signal progress bar for non-silent requests
    if (!config._silent) {
      progressBar.start();
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Intercepteur pour gérer les erreurs d'authentification et les requêtes annulées
api.interceptors.response.use(
  (response) => {
    if (!response.config?._silent) {
      progressBar.done();
    }
    return response;
  },
  (error) => {
    // Always finish progress bar on error
    if (!error.config?._silent) {
      progressBar.done();
    }

    // Gérer les requêtes annulées (AbortError, CanceledError)
    if (error.name === 'AbortError' || error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
      return Promise.resolve({ data: null, canceled: true });
    }

    // Gérer les erreurs de rate limiting (429)
    if (error.response?.status === 429) {
      console.warn('Rate limit atteint:', error.response.data?.message || 'Too Many Requests');
      error.isRateLimitError = true;
      error.userMessage = 'Trop de requêtes. Réessayez dans quelques instants.';
    }

    // 401 = token invalide/expiré → Auth0 logout
    if (error.response?.status === 401 && _auth0Logout && !_loggingOut) {
      _loggingOut = true;
      clearEventDetailsCache();
      _auth0Logout({ logoutParams: { returnTo: window.location.origin } });
    }

    return Promise.reject(error);
  }
);

// Récupérer un événement par ID (retourne nom, date, etc.)
export const getEventById = async (eventId) => {
  const response = await api.get(`/events/${eventId}`);
  return response.data;
};

// Recherche rapide d'événements (autocomplétion). `signal` permet d'annuler la
// requête précédente quand l'utilisateur continue de taper.
// requireIdEventPay : seulement les events avec un ID cashless (recherche d'un compte cashless).
export const quickSearchEvents = async (q, { limit = 12, signal, requireIdEventPay = false } = {}) => {
  const response = await api.get('/events/search', { params: { q, limit, ...(requireIdEventPay ? { requireIdEventPay: 'true' } : {}) }, signal });
  return response.data?.results || [];
};

// Recherche d'un event Pay (cashless) ou Online (billetterie) par nom, pour renseigner
// id_event_pay / id_event_online. `near` (AAAA-MM-JJ) fait remonter les events proches de
// cette date ; `excludeEventId` ignore la fiche courante dans le signalement « déjà lié ».
// `linked: false` : pas de signalement « déjà lié » (noms seuls, aucune lecture Mongo côté API).
export const searchPlatformEvents = async (source, q, { near, excludeEventId, signal, linked = true } = {}) => {
  const response = await api.get('/events/platform-search', {
    params: { source, q, near, excludeEventId, ...(linked ? {} : { linked: 0 }) },
    signal,
  });
  return response.data || { results: [], available: false };
};

// Event Pay / Online retrouvé par son ID exact dans le catalogue (null si absent).
export const findPlatformEventById = async (source, id, opts) => {
  const data = await searchPlatformEvents(source, id, opts);
  return (data.results || []).find((r) => r.id === id) || null;
};

// Billetteries supplémentaires d'un event (la principale reste id_event_online).
export const updateOnlineExtraIds = async (eventId, ids) => {
  const response = await api.put(`/events/${eventId}/online-extra`, { ids });
  return response.data?.ids_event_online_extra || [];
};

// Récupérer une commande (Order) depuis la base CouchDB wilout par ID
export const getWiloutOrder = async (orderId) => {
  const response = await api.get(`/couchdb/wilout/orders/${orderId}`);
  return response.data;
};

// Commandes d'un client par email — idEvent optionnel pour restreindre à un
// événement (sinon tout l'historique d'achat du client), même endpoint que le mobile.
export const searchWiloutOrdersByEmail = async (email, idEvent) => {
  const response = await api.get('/couchdb/wilout/orders/search', { params: { email, ...(idEvent ? { idEvent } : {}) } });
  return response.data?.orders || [];
};

// Billets émis d'une commande (collection tickets) : QR = keyRandom, scanned/quantity.
export const getWiloutOrderIssuedTickets = async (orderId) => {
  const response = await api.get(`/couchdb/wilout/orders/${orderId}/tickets`);
  return response.data?.tickets || [];
};

// Comptes cashless (wallets) d'un client sur UN event — idEvent obligatoire : `wallets`
// n'est indexé que par event côté wilout. idEvent = ID de la fiche event OP.
export const searchWiloutWalletsByEmail = async (email, idEvent) => {
  const response = await api.get('/couchdb/wilout/wallets/search', { params: { email, idEvent } });
  return response.data?.wallets || [];
};

// Transactions d'un wallet, rapprochées par ses clés NFC (wallet.keyList = keyRandomNFC).
export const getWiloutWalletTransactions = async (idEvent, keys) => {
  if (!idEvent || !keys?.length) return [];
  const response = await api.get('/couchdb/wilout/wallets/transactions', { params: { idEvent, uids: keys.join(',') } });
  return response.data?.transactions || [];
};

// Vérification d'un support NFC cashless sur un event OP : { key } (UID) ou { shortTag }.
export const getWiloutNfcSupport = async (eventId, query) => {
  const response = await api.get('/couchdb/wilout/nfc-supports', { params: { idEvent: eventId, ...query } });
  return response.data;
};

// ===== TÉLÉPHONIE VOIP (historique des appels OVHcloud) =====

export const getVoipCalls = async (filters = {}, { limit = 50, offset = 0 } = {}) => {
  const params = { limit, offset };
  if (filters.dateFrom) params.dateFrom = filters.dateFrom;
  if (filters.dateTo) params.dateTo = filters.dateTo;
  if (filters.chefEmail) params.chefEmail = filters.chefEmail;
  if (filters.lineId) params.lineId = filters.lineId;
  if (filters.direction) params.direction = filters.direction;
  if (filters.status) params.status = filters.status;
  if (filters.number) params.number = filters.number;
  const res = await api.get('/voip/calls', { params });
  return { ...res.data, results: res.data.items, hasMore: (res.data.offset || 0) + (res.data.count || 0) < (res.data.total || 0) };
};

export const getVoipLines = async () => {
  const res = await api.get('/voip/lines');
  return res.data.items;
};

export const createVoipLine = async (data) => {
  const res = await api.post('/voip/lines', data);
  return res.data;
};

export const updateVoipLine = async (id, data) => {
  const res = await api.patch(`/voip/lines/${id}`, data);
  return res.data;
};

export const deleteVoipLine = async (id) => {
  const res = await api.delete(`/voip/lines/${id}`);
  return res.data;
};

export const triggerVoipSync = async (from) => {
  const res = await api.post('/voip/sync', from ? { from } : {});
  return res.data;
};

// Récupérer un document wilout générique par ID
export const getWiloutDoc = async (docId) => {
  const response = await api.get(`/couchdb/wilout/docs/${docId}`);
  return response.data;
};

// Récupérer les appareils CouchDB.
// `includeLost`: inclut aussi les devices "perdu confirmé" (exclus du parc actif par
// défaut côté API, cf. GET /couchdb/devices) — nécessaire pour tout affichage qui veut
// compter les pertes plutôt que le seul parc déployable.
export const getCouchDevices = async ({ includeLost = false } = {}) => {
  try {
    const response = await api.get('/couchdb/devices', { params: includeLost ? { includeLost: 1 } : {} });
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la récupération des appareils CouchDB:', error);
    throw error;
  }
};

// Statut en ligne + batterie des terminaux Sunmi, lus dans le cache MDM côté API
// (aucun appel Sunmi). `updatedAt` = date du dernier rafraîchissement du cache.
export const getCouchDevicesMdmStatus = async (serials) => {
  const response = await api.post('/couchdb/devices/mdm-status', { serials });
  return response.data;
};

// Statut en ligne en direct (Sunmi onlineStatus, mémoire d'1 min côté API) — statut et
// heure du dernier changement seulement, la batterie reste celle du cache.
export const getCouchDevicesLiveStatus = async (serials) => {
  const response = await api.post('/couchdb/devices/mdm-status/live', { serials });
  return response.data;
};

// Récupérer les emails des organisateurs pour l'autocomplétion
export const getOrganizerEmails = async () => {
  try {
    const response = await api.get('/couchdb/organizers/emails');
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la récupération des emails des organisateurs:', error);
    throw error;
  }
};

// Fiche organisateur. null = introuvable (404) ; toute autre erreur (réseau, 500) remonte à
// l'appelant, pour ne pas proposer de « délier » une fiche simplement injoignable.
export const getOrganizerById = async (id) => {
  if (!id) return null;
  try {
    const response = await api.get(`/organizers/${id}`);
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) return null;
    throw error;
  }
};


// ===== PROFILS ORGANISATEUR (collection `organizers`, contacts multiples) =====
// getOrganizerById ci-dessus lit GET /organizers/:id (null si introuvable, lève sinon).
export const listOrganizers = async ({ q, limit = 50, offset = 0 } = {}) => {
  const res = await api.get('/organizers', { params: { q: q || undefined, limit, offset } });
  return res.data;
};

export const createOrganizer = async (data) => {
  const res = await api.post('/organizers', data);
  return res.data;
};

export const updateOrganizer = async (id, data) => {
  const res = await api.patch(`/organizers/${id}`, data);
  return res.data;
};

// 409 tant que des events sont liés à la fiche.
export const deleteOrganizer = async (id) => {
  await api.delete(`/organizers/${id}`);
};

// Events liés à la fiche (bornés à l'équipe de l'utilisateur) : { items, count }.
export const getOrganizerEvents = async (id) => {
  const res = await api.get(`/organizers/${id}/events`);
  return res.data;
};

// Fiches probables pour un event sans fiche : { items: [{ organizer, reasons }] }.
// Comptes Sellsy posés sur les events de la fiche mais pas encore sur la fiche (suggestions d'édition).
export const getOrganizerSellsySuggestions = async (id) => {
  const res = await api.get(`/organizers/${id}/sellsy-suggestions`);
  return res.data.items;
};

export const getOrganizerSuggestions = async (eventId) => {
  const res = await api.get(`/events/${eventId}/organizer-suggestions`);
  return res.data;
};

// Comptes organisateur de l'event sur les plateformes, lus dans Pay / la billetterie (jamais
// d'après email_orga) : { pay: { organizerId, email, source } | null, online: { account } | null }.
export const getEventPlatformAccounts = async (eventId) => {
  const res = await api.get(`/events/${eventId}/platform-accounts`, { _silent: true });
  return res.data;
};

// Absorbe la fiche `sourceId` (contacts + events) dans `targetId`, puis la supprime.
export const mergeOrganizers = async (targetId, sourceId) => {
  const res = await api.post(`/organizers/${targetId}/merge`, { source_id: sourceId });
  return res.data;
};

// Reprise des fiches depuis l'ID organisation des events : propositions, puis validation une à une.
export const getOrganizerBootstrapPlan = async () => {
  const res = await api.get('/organizers/bootstrap/plan');
  return res.data;
};

export const applyOrganizerBootstrap = async ({ idOrganisation, nom, acceptReview = false }) => {
  const res = await api.post('/organizers/bootstrap/apply', { id_organisation: idOrganisation, nom, accept_review: acceptReview });
  return res.data;
};

// Lie un event à une fiche organisateur (organizerRef '' = délier) et fixe le rôle de chaque
// contact sur cet event : contacts = [{ contact_id, role, principal }] (un seul principal).
export const linkEventOrganizer = async (eventId, organizerRef, contacts = []) => {
  const res = await api.put(`/events/${eventId}/organizer`, { organizer_ref: organizerRef, organizer_contacts: contacts });
  return res.data;
};

// ===== FLAGS TERMINAUX =====

export const updateDeviceFlag = async (deviceId, flag, reason, changedBy, eventId = null, eventName = null) => {
  const res = await api.put(`/couchdb/devices/${deviceId}/flag`, { flag, reason, changedBy, eventId, eventName });
  invalidateScanDeviceCatalog();
  return res.data;
};

// Modifier l'UUID d'un terminal
export const updateDeviceUuid = async (deviceId, uuid, changedBy) => {
  const res = await api.put(`/couchdb/devices/${deviceId}/uuid`, { uuid, changedBy });
  return res.data;
};

export const getTerminalMovements = async (limit = 50, deviceRef = '', offset = 0, flagFilter = '', eventId = '', extra = {}) => {
  const params = { limit, offset };
  if (deviceRef) params.deviceRef = deviceRef;
  // Historique d'UN terminal : égalité stricte (sinon M-LSP-1 remonte aussi M-LSP-10…19).
  if (deviceRef && extra.exactRef) params.exactRef = 1;
  // Filtre exact par id (fiche terminal) : indexé côté API, contrairement à deviceRef (recherche partielle).
  if (extra.deviceId) params.deviceId = extra.deviceId;
  if (flagFilter) params.flagFilter = flagFilter;
  if (eventId) params.eventId = eventId;
  if (extra.direction) params.direction = extra.direction;
  if (extra.associationType) params.associationType = extra.associationType;
  if (extra.region) params.region = extra.region;
  // 'movement' | 'sim_verification' — l'historique mêle les deux par défaut
  if (extra.kind) params.kind = extra.kind;
  const res = await api.get('/couchdb/devices-movements', { params });
  const d = res.data;
  // Enveloppe REST { items, count, total, limit, offset } + compat (results/hasMore)
  return { ...d, results: d.items, hasMore: (d.offset || 0) + (d.count || 0) < (d.total || 0) };
};

// ===== CATALOGUE DE SCAN (parc terminaux allégé) =====

// La modale de scan résout les QR côté client : il lui faut tout le parc en mémoire (sur
// le terrain, la connexion ne permet pas un aller-retour par scan). Deux garde-fous ici :
// - /couchdb/devices/catalog ne renvoie que les champs utiles au scan, là où
//   /couchdb/devices renvoie les documents complets + l'annuaire organisateurs ;
// - le résultat est mémorisé au niveau du module, sinon chaque montage (ouverture d'une
//   fiche event, retour sur l'onglet) retire tout le parc.
const SCAN_CATALOG_TTL_MS = 5 * 60 * 1000;
let scanCatalogCache = null;    // { at, devices }
let scanCatalogInflight = null; // dédoublonne les appels concurrents

export const invalidateScanDeviceCatalog = () => { scanCatalogCache = null; };

export const getScanDeviceCatalog = async () => {
  if (scanCatalogCache && Date.now() - scanCatalogCache.at < SCAN_CATALOG_TTL_MS) {
    return scanCatalogCache.devices;
  }
  if (scanCatalogInflight) return scanCatalogInflight;
  scanCatalogInflight = api.get('/couchdb/devices/catalog')
    .then((res) => {
      const devices = Array.isArray(res.data) ? res.data : (res.data?.devices || []);
      scanCatalogCache = { at: Date.now(), devices };
      return devices;
    })
    .finally(() => { scanCatalogInflight = null; });
  return scanCatalogInflight;
};

// ===== SCAN ENTREE/SORTIE & STOCK TERMINAUX =====

// Scan d'un terminal = création d'un mouvement (entrée/sortie).
// payload: { association: { type, idOrga?|orgaEmail?, eventId? }, date_retour_prevue?, delivery?, changedBy? }
export const createDeviceMovement = async (deviceId, payload) => {
  const res = await api.post(`/couchdb/devices/${deviceId}/movements`, payload);
  // L'état (flag/détenteur) du terminal vient de changer : le catalogue mémorisé est périmé.
  invalidateScanDeviceCatalog();
  return res.data;
};

// Récapitulatif global du stock terminaux (admin)
export const getDeviceStockRecap = async () => {
  const res = await api.get('/couchdb/devices/stock-recap');
  return res.data;
};

// Rapport terrain (app mobile) : classer / rouvrir — auteur ou admin uniquement côté API.
export const setFieldReportStatus = async (id, status) => {
  const res = await api.patch(`/field-reports/${encodeURIComponent(id)}/status`, { status });
  return res.data;
};

// Corrige les idOrga CouchDB périmés (terminaux déjà rentrés côté mouvements Mongo, cf.
// cron quotidien deviceOrgaAutoFix.js) — déclenchable à la demande sans attendre le cron.
export const fixDeviceOrga = async (dryRun = false) => {
  const res = await api.post('/couchdb/devices/fix-orga', { dryRun });
  return res.data;
};

// Compteurs SIMs Jasper (activées, activées ce mois) — agrégés côté serveur
export const getSimStats = async () => {
  const res = await api.get('/api/jasper/sims/stats');
  return res.data;
};

// Calendrier du stock terminaux à venir par event
export const getUpcomingStockCalendar = async (from, to) => {
  const params = {};
  if (from) params.from = from;
  if (to) params.to = to;
  const res = await api.get('/logistique/stock/devices/calendar', { params });
  return res.data;
};

// Matériel terminaux d'un event (sortis / rentrés / dates)
export const getEventDevices = async (eventId) => {
  const res = await api.get(`/events/${eventId}/devices`);
  return res.data;
};

// Remboursements cashless de l'event (sur place, demandes en ligne par statut, périodes) — cache 15 min côté API.
export const getEventCashlessRefunds = async (eventId, { refresh = false } = {}) => {
  const res = await api.get(`/events/${eventId}/cashless-refunds`, { params: refresh ? { refresh: 1 } : undefined });
  return res.data;
};

// Lignes de l'export CSV des remboursements (une par transaction, identité du wallet sans IBAN) — cache 30 s côté API.
export const getEventCashlessRefundsExport = async (eventId) => {
  const res = await api.get(`/events/${eventId}/cashless-refunds/export`);
  return res.data;
};

// Séances (calendrier) des billetteries de l'event, avec leur nombre de billets.
export const getEventTicketingSessions = async (eventId) => {
  const res = await api.get(`/events/${eventId}/ticketing-sessions`);
  return res.data;
};

// Lignes de l'export des participants d'une séance (un billet par ligne, nom/prénom/email).
export const getEventSessionParticipantsExport = async (eventId, scheduleId) => {
  const res = await api.get(`/events/${eventId}/ticketing-sessions/${encodeURIComponent(scheduleId)}/participants/export`);
  return res.data;
};

// Liens de partage de l'export par séance (page publique client) : liste, création (le token n'est
// renvoyé qu'ici), révocation.
export const getSessionExportLinks = async (eventId) => {
  const res = await api.get(`/events/${eventId}/ticketing-sessions/export-links`);
  return res.data;
};

export const createSessionExportLink = async (eventId, days) => {
  const res = await api.post(`/events/${eventId}/ticketing-sessions/export-links`, { days });
  return res.data;
};

export const revokeSessionExportLink = async (eventId, linkId) => {
  await api.delete(`/events/${eventId}/ticketing-sessions/export-links/${encodeURIComponent(linkId)}`);
};

// Alertes de remplissage des séances (90 %, complète, dépassement, bloquée) : activation et adresses
// prévenues en plus du chef de projet.
export const getSessionAlertSettings = async (eventId) => {
  const res = await api.get(`/events/${eventId}/ticketing-sessions/alerts`);
  return res.data;
};

export const saveSessionAlertSettings = async (eventId, { enabled, emails }) => {
  const res = await api.put(`/events/${eventId}/ticketing-sessions/alerts`, { enabled, emails });
  return res.data;
};

// Page publique du client (aucune authentification, lien à token) : séances, export d'une séance, export complet.
export const getPublicSessionExport = async (token) => {
  const res = await api.get(`/public/session-exports/${encodeURIComponent(token)}`, { _silent: true });
  return res.data;
};

export const getPublicSessionParticipants = async (token, scheduleId) => {
  const res = await api.get(`/public/session-exports/${encodeURIComponent(token)}/sessions/${encodeURIComponent(scheduleId)}/participants`, { _silent: true });
  return res.data;
};

export const getPublicAllSessionsParticipants = async (token) => {
  const res = await api.get(`/public/session-exports/${encodeURIComponent(token)}/participants`, { _silent: true });
  return res.data;
};

// Page publique de saisie d'IBAN d'un participant (dossier de remboursement, lien à token, sans compte).
export const getRefundBankLink = async (token) => {
  const res = await api.get(`/public/refund-cases/${encodeURIComponent(token)}`, { _silent: true });
  return res.data;
};

export const submitRefundBankLink = async (token, data) => {
  const res = await api.post(`/public/refund-cases/${encodeURIComponent(token)}`, data, { _silent: true });
  return res.data;
};

// Bilan cashless d'un organisateur (onglet Cashless) : recherche (email, nom d'event) puis bilan de ses
// events Pay. Lecture seule, canAccessTerminals ; le calcul peut durer plusieurs secondes (cache 15 min côté API).
export const searchCashlessOrganizers = async (q) => {
  const res = await api.get('/cashless-organizers', { params: { q } });
  return res.data;
};

export const getOrganizerCashlessReport = async ({ organizerId, email }, { refresh = false } = {}) => {
  const res = await api.get('/cashless-organizers/reports', {
    params: { organizerId: organizerId || undefined, email: email || undefined, refresh: refresh ? 1 : undefined },
  });
  return res.data;
};

// Bilan billetterie d'une fiche organisateur (onglet Billetterie) : toutes les billetteries des events
// liés à la fiche (bornés à l'équipe). canViewEvents ; calcul de quelques secondes, cache 15 min côté API.
export const getOrganizerTicketingReport = async (organizerId, { refresh = false } = {}) => {
  const res = await api.get(`/organizers/${encodeURIComponent(organizerId)}/ticketing-report`, {
    params: refresh ? { refresh: 1 } : undefined,
  });
  return res.data;
};

// « Prêt pour l'event ? » : contrôle de la configuration de l'event Pay lié (cache 5 min côté API).
export const getEventPayReadiness = async (eventId, { refresh = false } = {}) => {
  const res = await api.get(`/events/${eventId}/pay-readiness`, { params: refresh ? { refresh: 1 } : undefined });
  return res.data;
};

// Récap des demandes de remboursement billetterie (à traiter / traités, demandes en attente paginées : pendingRows { items, total, limit, offset }) — cache 15 min côté API.
export const getEventTicketingRefunds = async (eventId, { refresh = false, offset = 0 } = {}) => {
  const res = await api.get(`/events/${eventId}/ticketing-refunds`, { params: { ...(refresh ? { refresh: 1 } : {}), offset } });
  return res.data;
};

// Demandes de remboursement billetterie traitées : recherche email / nom / prénom / référence, pages de 30
// ({ items (+ reported), total, limit, offset }) — filtre côté API sur le cache du récap.
export const searchEventTicketingRefundsDone = async (eventId, { q = '', offset = 0 } = {}) => {
  const res = await api.get(`/events/${eventId}/ticketing-refunds/done`, { params: { ...(q ? { q } : {}), offset, limit: 30 } });
  return res.data;
};

// Dossiers d'erreurs de remboursement d'un event : { items, total, counts par statut }.
export const getEventRefundCases = async (eventId, { status } = {}) => {
  const res = await api.get(`/events/${eventId}/refund-cases`, { params: { limit: 200, ...(status ? { status } : {}) } });
  return res.data;
};

// Préremplissage d'un dossier depuis une commande ou une demande de remboursement de l'event.
export const lookupRefundCaseOrder = async (eventId, orderId) => {
  const res = await api.get(`/events/${eventId}/refund-cases/lookup-order/${encodeURIComponent(orderId)}`);
  return res.data;
};

export const createRefundCase = async (eventId, payload) => {
  const res = await api.post(`/events/${eventId}/refund-cases`, payload);
  return res.data;
};

export const updateRefundCase = async (eventId, caseId, payload) => {
  const res = await api.patch(`/events/${eventId}/refund-cases/${caseId}`, payload);
  return res.data;
};

export const changeRefundCaseStatus = async (eventId, caseId, payload) => {
  const res = await api.post(`/events/${eventId}/refund-cases/${caseId}/status-changes`, payload);
  return res.data;
};

export const addRefundCaseNote = async (eventId, caseId, text) => {
  const res = await api.post(`/events/${eventId}/refund-cases/${caseId}/notes`, { text });
  return res.data;
};

export const setRefundCaseBankDetails = async (eventId, caseId, payload) => {
  const res = await api.put(`/events/${eventId}/refund-cases/${caseId}/bank-details`, payload);
  return res.data;
};

// Envoie au participant l'email de demande de coordonnées (ou la relance) ; le dossier passe en attente du client.
export const requestRefundCaseEmail = async (eventId, caseId) => {
  const res = await api.post(`/events/${eventId}/refund-cases/${caseId}/request-email`);
  return res.data;
};

// Lignes de l'export compta (IBAN en clair, dossiers aux coordonnées reçues) ; le CSV est fabriqué par le front.
export const getEventRefundCasesExport = async (eventId) => {
  const res = await api.get(`/events/${eventId}/refund-cases/export`);
  return res.data;
};

// Vue globale (admin) des dossiers de remboursement de tous les events : { items (avec eventName, currency), total,
// counts et amounts par statut (montants par devise), events (choix du filtre) }. q = participant / email,
// mine = dossiers dont je suis responsable, sort = 'asc' | 'desc' sur la dernière modification.
export const listAllRefundCases = async ({ status, eventId, q, mine = false, sort = 'desc', offset = 0, limit = 50 } = {}) => {
  const res = await api.get('/refund-cases', {
    params: { limit, offset, sort, ...(status ? { status } : {}), ...(eventId ? { eventId } : {}), ...(q ? { q } : {}), ...(mine ? { mine: 1 } : {}) },
  });
  return res.data;
};

// Demandes de remboursement billetterie en attente et pas encore signalées, tous events confondus :
// { computedAt, truncated, items (avec eventId, eventName, currency), unlinked, unlinkedEvents }. Cache 15 min côté API.
export const getPendingRefundRequests = async ({ refresh = false } = {}) => {
  const res = await api.get('/refund-cases/pending-requests', { params: refresh ? { refresh: 1 } : {} });
  return res.data;
};

// Export compta global : scope 'a_virer' (coordonnées reçues, IBAN en clair), 'tous' ou un statut ; { rows, truncated }.
export const getAllRefundCasesExport = async (scope = 'a_virer') => {
  const res = await api.get('/refund-cases/export', { params: { scope } });
  return res.data;
};

// « Prêt pour l'event ? » côté billetterie : toutes les billetteries liées (cache 5 min côté API).
export const getEventTicketingReadiness = async (eventId, { refresh = false } = {}) => {
  const res = await api.get(`/events/${eventId}/ticketing-readiness`, { params: refresh ? { refresh: 1 } : undefined });
  return res.data;
};

// « Prêt pour l'event ? » côté contrôle : listes contenant les tarifs de l'event et accès contrôleurs.
export const getEventControlReadiness = async (eventId, { refresh = false } = {}) => {
  const res = await api.get(`/events/${eventId}/control-readiness`, { params: refresh ? { refresh: 1 } : undefined });
  return res.data;
};

// --- État exceptionnel d'un terminal (HS / en réparation / perdu) ---
// Overlay MongoDB (device_state) fusionné dans les devices renvoyés par /couchdb/devices —
// ces trois actions sont les seules à appeler directement, le reste (HS/réparation) passe
// par les tickets de réparation (cf. plus bas).
export const markDeviceLostPending = async (deviceId, reason, deviceRef) => {
  const res = await api.post(`/device-state/${deviceId}/lost-pending`, { reason, deviceRef });
  invalidateScanDeviceCatalog();
  return res.data;
};

export const confirmDeviceLost = async (deviceId, reason) => {
  const res = await api.post(`/device-state/${deviceId}/lost-confirmation`, { reason });
  invalidateScanDeviceCatalog();
  return res.data;
};

// Remise à l'état normal (HS corrigé hors ticket, ou pad "perdu à confirmer" retrouvé).
// Refuse côté backend sur LOST_CONFIRMED (irréversible par ce chemin) et IN_REPAIR (passer
// par la clôture du ticket).
export const clearDeviceState = async (deviceId) => {
  const res = await api.delete(`/device-state/${deviceId}`);
  invalidateScanDeviceCatalog();
  return res.data;
};

export const getDeviceStates = async (params = {}) => {
  const res = await api.get('/device-state', { params });
  return res.data.items;
};

// Fiche de recherche d'un terminal HS/perdu (mouvements, MDM Sunmi en direct, position + adresse).
export const getDeviceInvestigation = async (deviceId) => {
  const res = await api.get(`/device-state/${encodeURIComponent(deviceId)}/investigation`);
  return res.data;
};

// --- Tickets de réparation matériel ---
export const getRepairTickets = async (params = {}) => {
  const res = await api.get('/repair-tickets', { params });
  return res.data;
};

export const getRepairTicket = async (id) => {
  const res = await api.get(`/repair-tickets/${id}`);
  return res.data;
};

export const createRepairTicket = async (data) => {
  const res = await api.post('/repair-tickets', data);
  invalidateScanDeviceCatalog();
  return res.data;
};

export const updateRepairTicket = async (id, data) => {
  const res = await api.patch(`/repair-tickets/${id}`, data);
  return res.data;
};

export const addRepairTicketStatus = async (id, data) => {
  const res = await api.post(`/repair-tickets/${id}/status-changes`, data);
  invalidateScanDeviceCatalog();
  return res.data;
};

// --- Actifs suivis à l'unité hors CouchDB (Starlink) ---
export const getAssetUnits = async (params = {}) => {
  const res = await api.get('/asset-units', { params });
  return res.data.items;
};

export const createAssetUnit = async (data) => {
  const res = await api.post('/asset-units', data);
  return res.data;
};

export const retireAssetUnit = async (id) => {
  const res = await api.patch(`/asset-units/${id}/retire`);
  return res.data;
};

export const createAssetUnitMovement = async (id, data) => {
  const res = await api.post(`/asset-units/${id}/movements`, data);
  return res.data;
};

// --- Suivi d'envois transporteur (liés à un event) ---
// Vidéos à voir avant d'accéder à opé (api routes/requiredVideos.ts).
export const getRequiredVideos = async () => {
  const res = await api.get('/required-videos/me');
  return res.data;
};

export const getRequiredVideoUrl = async (key) => {
  const res = await api.get(`/required-videos/${encodeURIComponent(key)}/url`);
  return res.data;
};

export const postRequiredVideoProgress = async (key, position) => {
  const res = await api.post(`/required-videos/${encodeURIComponent(key)}/progress`, { position });
  return res.data;
};

export const getShipments = async (params = {}) => {
  const res = await api.get('/shipments', { params });
  return res.data;
};

export const getEventShipments = async (eventId) => {
  const res = await api.get('/shipments', { params: { eventId } });
  return res.data.items;
};

// Créé/complète silencieusement un envoi depuis un lot de scan sortie vers un event (cf.
// ScanModal.js) — aucune saisie de plus pour le CDP, pas de formulaire de création dédié.
export const upsertShipmentFromScan = async (data) => {
  const res = await api.post('/shipments/from-scan', data);
  return res.data;
};

// Fin de déploiement : demande au backend la push "numéro Chronopost à renseigner" (une
// seule fois par envoi, no-op si pas Chronopost ou numéro déjà saisi). Renvoie { sent }.
export const requestShipmentTrackingReminder = async (eventId) => {
  const res = await api.post('/shipments/tracking-reminder', { eventId });
  return res.data;
};

export const addShipmentStatus = async (id, data) => {
  const res = await api.post(`/shipments/${id}/status-changes`, data);
  invalidateScanDeviceCatalog(); // une perte transporteur peut faire passer des devices en lost_pending
  return res.data;
};

// Renseigner/corriger transporteur, numéro de suivi... après coup (ex. numéro de suivi
// Chronopost/Colissimo reçu après le départ du colis, pas encore connu au moment du scan).
export const updateShipment = async (id, data) => {
  const res = await api.patch(`/shipments/${id}`, data);
  return res.data;
};

// Suivi live (API "Suivi" La Poste, couvre aussi Chronopost/Colissimo) — 501 tant que
// LAPOSTE_API_KEY n'est pas configurée côté serveur, l'appelant doit gérer ce cas sans alarmer.
export const getShipmentTracking = async (id) => {
  const res = await api.get(`/shipments/${id}/tracking`);
  return res.data;
};

// Admin uniquement côté backend (verifyAdmin) — pour un envoi de test/erroné, pas une action
// de gestion courante.
export const deleteShipment = async (id) => {
  const res = await api.delete(`/shipments/${id}`);
  return res.data;
};

// --- Bons retour Chronopost préparés en avance (glissés dans les mallettes au départ) ---
export const getEventReturnLabels = async (eventId) => {
  const res = await api.get('/return-labels', { params: { eventId } });
  return res.data.items;
};

export const createReturnLabel = async (data) => {
  const res = await api.post('/return-labels', data);
  return res.data;
};

export const setReturnLabelStatus = async (id, statut) => {
  const res = await api.post(`/return-labels/${id}/status`, { statut });
  return res.data;
};

// { tracking, label } : le statut du bon avance tout seul si le colis a été déposé/livré.
export const getReturnLabelTracking = async (id) => {
  const res = await api.get(`/return-labels/${id}/tracking`);
  return res.data;
};

export const deleteReturnLabel = async (id) => {
  const res = await api.delete(`/return-labels/${id}`);
  return res.data;
};

// Bon de livraison d'un event (renseigné par le CDP)
export const updateEventBonLivraison = async (eventId, fields) => {
  const res = await api.patch(`/events/${eventId}/bon-livraison`, fields);
  return res.data;
};

// Numéro(s) de mallette d'un event — même champ top-level "numero_mallettes" que la
// fiche event, mis à jour ici depuis le scan sortie/retour. Route dédiée (même garde que le
// bon de livraison) : tout CDP peut déployer, même sur un event dont il n'est pas le chef.
export const updateEventNumeroMallettes = async (eventId, mallettes = []) => {
  const cleaned = (Array.isArray(mallettes) ? mallettes : []).filter(Boolean).filter((v) => v !== 'Pas de mallette');
  const res = await api.patch(`/events/${eventId}/numero-mallettes`, { mallettes: cleaned.map((item) => item.trim()) });
  return res.data;
};

// Dispatch automatique des terminaux : points de l'event Pay, chaque pad placé sur le point de
// sa dernière transaction (terminals = encore dehors, returned = rentrés). Lecture seule.
export const getEventDispatch = async (eventId) => {
  const res = await api.get(`/events/${eventId}/dispatch`);
  return res.data;
};

// Prêts de pads en démo (commerciaux) : liste, sortie (code = ref ou n° de série scanné), retour.
export const getDemoLoans = async () => {
  const res = await api.get('/demo-loans');
  return res.data;
};

// Pads disponibles en stock, filtrés par les premiers caractères de la référence (q).
export const getAvailableDemoDevices = async (q = '') => {
  const res = await api.get('/demo-loans/available', { params: { q, limit: 20 } });
  return res.data;
};

export const createDemoLoan = async (code, client) => {
  const res = await api.post('/demo-loans', { code, client });
  return res.data;
};

export const returnDemoLoan = async (deviceId) => {
  const res = await api.delete(`/demo-loans/${deviceId}`);
  return res.data;
};

// Journées d'exploitation de l'event (J1, J2…), lues dans les transactions de l'event Pay.
export const getEventDispatchDays = async (eventId) => {
  const res = await api.get(`/events/${eventId}/dispatch/days`);
  return res.data;
};

// Position des pads sur une journée (day = AAAA-MM-JJ) : point de leur dernière transaction ce jour-là.
export const getEventDispatchDay = async (eventId, day) => {
  const res = await api.get(`/events/${eventId}/dispatch/days/${day}`);
  return res.data;
};

// Contrôle d'accès : listes de contrôle rattachées à l'event + zones d'entrée (listes
// regroupées par entrée physique, affichées en totaux sur l'app mobile)
export const getEventControlLists = async (eventId) => {
  const res = await api.get(`/events/${eventId}/control-lists`);
  return res.data;
};

export const updateEventZonesControle = async (eventId, zones_controle) => {
  const res = await api.patch(`/events/${eventId}/zones-controle`, { zones_controle });
  return res.data;
};

// « Module config interne » : services (PAY / ONLINE / CONTROL) activés pour de la config interne,
// déclarés prêts quels que soient leurs manques. Remplacement complet de la liste.
export const updateEventConfigInterne = async (eventId, config_interne) => {
  const res = await api.patch(`/events/${eventId}/config-interne`, { config_interne });
  return res.data;
};

// Codes d'ouverture des terminaux (banque / point de vente / controle)
export const updateEventCodesAcces = async (eventId, codes_acces) => {
  const res = await api.patch(`/events/${eventId}/codes-acces`, { codes_acces });
  return res.data;
};

// Sous-checklist d'une tâche (formation back-office…) : instantané complet des points.
// L'API renvoie le statut déduit de la tâche parente + checklist_metadata / checklist relus.
export const updateEventSubChecklist = async (eventId, task, items) => {
  const res = await api.put(`/events/${eventId}/sub-checklist`, {
    task,
    items: items.map(({ key, label, section, hint, services, state }) => ({
      key,
      label,
      ...(section ? { section } : {}),
      ...(hint ? { hint } : {}),
      ...(services?.length ? { services } : {}),
      state,
    })),
  });
  return res.data;
};

// Signature de la fiche par le CDP (PNG en data URL) — 409 si un point reste à voir.
export const signEventSubChecklist = async (eventId, task, signature) => {
  const res = await api.post(`/events/${eventId}/sub-checklist/sign`, { task, signature });
  return res.data;
};

export const reopenEventSubChecklist = async (eventId, task) => {
  const res = await api.post(`/events/${eventId}/sub-checklist/reopen`, { task });
  return res.data;
};

export const getEventSubChecklistSignature = async (eventId, task) => {
  const res = await api.get(`/events/${eventId}/sub-checklist/signature`, { params: { task } });
  return res.data;
};

// --- Comptes app mobile (admins) : identifiant + mot de passe, events autorisés ---
export const listMobileAccounts = async () => {
  const res = await api.get('/mobile-accounts');
  return res.data;
};

export const createMobileAccount = async ({ username, name, password, eventIds }) => {
  const res = await api.post('/mobile-accounts', { username, name, password, eventIds });
  return res.data;
};

export const updateMobileAccount = async (id, patch) => {
  const res = await api.patch(`/mobile-accounts/${id}`, patch);
  return res.data;
};

export const deleteMobileAccount = async (id) => {
  const res = await api.delete(`/mobile-accounts/${id}`);
  return res.data;
};

// Fonctionnalités de l'app ouvertes aux comptes app mobile (Admin › Niveaux d'accès).
export const getMobileAccountFeatures = async () => {
  const res = await api.get('/mobile-accounts/features');
  return res.data.features;
};

export const updateMobileAccountFeatures = async (features) => {
  const res = await api.put('/mobile-accounts/features', { features });
  return res.data.features;
};

// ===== DEPLACEMENTS & ACCOMPAGNANTS =====

export const getDeplacements = async () => {
  const res = await api.get('/accompagnants/deplacements');
  return res.data;
};

export const getAccompagnants = async () => {
  const res = await api.get('/accompagnants');
  return res.data;
};

export const createAccompagnant = async (data) => {
  const res = await api.post('/accompagnants', data);
  return res.data;
};

export const updateAccompagnant = async (id, data) => {
  const res = await api.put(`/accompagnants/${id}`, data);
  return res.data;
};

export const deleteAccompagnant = async (id) => {
  const res = await api.delete(`/accompagnants/${id}`);
  return res.data;
};

export const assignAccompagnantToEvent = async (eventId, accompagnantId, role = '', notes = '') => {
  const res = await api.post(`/accompagnants/events/${eventId}`, {
    accompagnant_id: accompagnantId,
    role,
    notes
  });
  return res.data;
};

export const removeAccompagnantFromEvent = async (eventId, accompagnantId) => {
  const res = await api.delete(`/accompagnants/events/${eventId}/${accompagnantId}`);
  return res.data;
};

export const updateDeplacementFields = async (eventId, fields) => {
  const res = await api.patch(`/accompagnants/deplacements/${eventId}/fields`, fields);
  return res.data;
};

export const updateFeuilleRoute = async (eventId, data) => {
  const res = await api.patch(`/events/${eventId}/feuille-route`, data);
  return res.data;
};

export const updateDeplacementNotes = async (eventId, notes) => {
  const res = await api.patch(`/accompagnants/deplacements/${eventId}/notes`, {
    notes_logistique: notes
  });
  return res.data;
};

// ===== CONVERTISSEUR D'ACCREDITATIONS (historique imports) =====

// Historique d'un contexte (festival/événement) pour dédupliquer les accrédités déjà exportés.
export const getAccreditationImports = async (context) => {
  const res = await api.get('/accreditations/imports', { params: { context } });
  return res.data;
};

// Enregistre les accrédités exportés (idempotent côté API).
export const recordAccreditationImports = async (context, sourceFile, items) => {
  const res = await api.post('/accreditations/imports', { context, sourceFile, items });
  return res.data;
};

// Supprime l'historique : { all: true } vide le contexte, sinon retire les identityKeys ciblés.
export const deleteAccreditationImports = async (context, { all = false, identityKeys = [] } = {}) => {
  const res = await api.delete('/accreditations/imports', {
    params: { context, all: all ? 'true' : undefined },
    data: { context, identityKeys },
  });
  return res.data;
};

// Mapping de conversion mémorisé (partagé), indexé par signature de colonnes.
export const getAccreditationMapping = async (signature) => {
  const res = await api.get('/accreditations/mappings', { params: { signature }, _silent: true });
  return res.data?.config || null;
};

export const saveAccreditationMapping = async (signature, config) => {
  const res = await api.post('/accreditations/mappings', { signature, config }, { _silent: true });
  return res.data;
};

// Réserve `count` codes-barres uniques (préfixe ac, longueur totale `length`) dans le registre
// central : un code ne sort jamais deux fois, tous événements confondus.
export const claimAccreditationBarcodes = async (context, count, length) => {
  const res = await api.post('/accreditations/barcodes', { context, count, length });
  return res.data.codes;
};

// QR codes en réserve (spares) d'un événement de la page Outils : générés par catégorie et
// journée, conservés en base, marquables « utilisés » ; réglages du générateur mémorisés.
export const getAccreditationSpares = async (context, filters = {}) =>
  (await api.get('/accreditations/spares', { params: { context, ...filters } })).data.items;
export const getAccreditationSpareBatches = async (context) =>
  (await api.get('/accreditations/spares/batches', { params: { context } })).data.batches;
export const generateAccreditationSpares = async (context, items, length, pin) =>
  (await api.post('/accreditations/spares', { context, items, length, pin })).data.items;
export const deleteAccreditationSpares = async (context, pin) =>
  (await api.delete('/accreditations/spares', { params: { context }, data: { pin } })).data.deleted;
export const unlockAccreditationSpares = async (context, pin) =>
  api.post('/accreditations/spares/unlock', { pin }, { _silent: true });
export const setAccreditationSpareUsed = async (context, id, used) =>
  (await api.patch(`/accreditations/spares/${id}`, { context, used })).data;
export const getAccreditationSpareSettings = async (context) =>
  (await api.get('/accreditations/spares/settings', { params: { context }, _silent: true })).data.settings;
export const saveAccreditationSpareSettings = async (context, settings) =>
  (await api.put('/accreditations/spares/settings', { context, settings }, { _silent: true })).data.settings;

// Récupère un export Weezevent (CSV, ex. Crew bénévoles) via le backend (évite le CORS).
export const fetchWeezeventExport = async (url) => {
  const res = await api.get('/accreditations/weezevent-export', { params: { url } });
  return res.data;
};

// ===== PROJETS D'ACCREDITATION (formulaires externes → CSV Wilout) =====
// Endpoints internes (employé Wilout authentifié, même instance `api` que le reste du fichier).

export const createAccreditationProject = async (data) => {
  const res = await api.post('/accreditations/projects', data);
  return res.data;
};

export const listAccreditationProjects = async () => {
  const res = await api.get('/accreditations/projects');
  return res.data;
};

export const getAccreditationProject = async (projectId) => {
  const res = await api.get(`/accreditations/projects/${projectId}`);
  return res.data;
};

export const revealAccreditationProjectLink = async (projectId) => {
  const res = await api.post(`/accreditations/projects/${projectId}/config-link/reveal`);
  return res.data;
};

export const regenerateAccreditationProjectLink = async (projectId) => {
  const res = await api.post(`/accreditations/projects/${projectId}/config-link/regenerate`);
  return res.data;
};

// Espace complet (formulaires + convertisseur) ou « convertisseur seul » (convertisseur + QR
// codes en réserve, formulaires et réponses désactivés) — { formsEnabled: boolean }.
export const updateAccreditationProjectSettings = async (projectId, settings) => {
  const res = await api.patch(`/accreditations/projects/${projectId}/settings`, settings);
  return res.data;
};

export const archiveAccreditationProject = async (projectId) => {
  const res = await api.delete(`/accreditations/projects/${projectId}`);
  return res.data;
};

export const getAccreditationProjectExport = async (projectId, mode = 'all') => {
  const res = await api.get(`/accreditations/projects/${projectId}/export`, { params: { mode } });
  return res.data;
};

// Lignes "TARIF/PREGEN" placeholder couvrant tous les tarifs possibles du projet (zone × jours
// de chaque formulaire) — à importer une fois dans Wilout pour les pré-créer avant les vraies
// demandes, même logique que la pré-génération du convertisseur fichier existant.
export const getAccreditationProjectPregen = async (projectId) => {
  const res = await api.get(`/accreditations/projects/${projectId}/pregen`);
  return res.data;
};

// ===== CONFIGURATEUR EXTERNE (lien magique — pas de token Auth0) =====
// Le token de config est transmis explicitement en header à chaque appel : ces fonctions
// s'appuient sur la même instance axios `api`, mais l'intercepteur Auth0 n'ajoute jamais de
// header ici puisque initApiAuth() n'est jamais appelé sur le point d'entrée public
// (voir index.js — arbre séparé, sans Auth0Provider).

const configTokenHeaders = (configToken) => ({ headers: { 'x-accreditation-config-token': configToken } });

// Codes d'accès (identifiant / mot de passe / clé d'accès) de l'event relié au projet.
// 404 si aucun event n'est relié : l'appelant l'affiche, ce n'est pas une panne.
export const getAccreditationAccessCodes = async (configToken) => {
  const res = await api.get('/accreditation-config/access-codes', configTokenHeaders(configToken));
  return res.data;
};

export const getAccreditationConfigSession = async (configToken) => {
  const res = await api.get('/accreditation-config/session', configTokenHeaders(configToken));
  return res.data;
};

// Jours de l'événement, partagés par tous les formulaires du projet (saisis une seule fois) —
// chaque formulaire en choisit ensuite un sous-ensemble (cf. AccreditationFormEditor).
export const updateAccreditationProjectEventDays = async (configToken, eventDays) => {
  const res = await api.put('/accreditation-config/event-days', { eventDays }, configTokenHeaders(configToken));
  return res.data;
};

// Types d'accès de l'événement (liste complète) : partagés par les formulaires « chef de pôle ».
// Un type déjà utilisé par des réponses ne peut pas être retiré (409).
export const updateAccreditationAccessTypes = async (configToken, accessTypes) => {
  const res = await api.put('/accreditation-config/access-types', { accessTypes }, configTokenHeaders(configToken));
  return res.data;
};

export const createAccreditationForm = async (configToken, data) => {
  const res = await api.post('/accreditation-config/forms', data, configTokenHeaders(configToken));
  return res.data;
};

export const updateAccreditationForm = async (configToken, formId, data) => {
  const res = await api.put(`/accreditation-config/forms/${formId}`, data, configTokenHeaders(configToken));
  return res.data;
};

export const closeAccreditationForm = async (configToken, formId) => {
  const res = await api.delete(`/accreditation-config/forms/${formId}`, configTokenHeaders(configToken));
  return res.data;
};

export const getAccreditationFormResponses = async (configToken, formId) => {
  const res = await api.get(`/accreditation-config/forms/${formId}/responses`, configTokenHeaders(configToken));
  return res.data;
};

export const regenerateAccreditationFormPublicLink = async (configToken, formId) => {
  const res = await api.post(`/accreditation-config/forms/${formId}/public-link/regenerate`, null, configTokenHeaders(configToken));
  return res.data;
};

// Journalise un export CSV brut (généré côté navigateur, jamais envoyé au serveur — seul
// l'événement mode+nombre de lignes est enregistré) et récupère l'historique de ces exports.
export const logAccreditationExport = async (configToken, { mode, rowCount }) => {
  const res = await api.post('/accreditation-config/export-log', { mode, rowCount }, configTokenHeaders(configToken));
  return res.data;
};

export const getAccreditationExportLog = async (configToken) => {
  const res = await api.get('/accreditation-config/export-log', configTokenHeaders(configToken));
  return res.data;
};

// Export "format Wilout" (colonnes tarif/cashless/code-barres, sans les champs personnalisés) —
// mêmes lignes que l'export interne réservé admins/CDP, calculées serveur-side (accreditationRowBuilder,
// seule source de vérité) pour que l'externe puisse réimporter directement dans Wilout.
// `since` (optionnel, Date ou ISO string) ne garde que les réponses soumises après cette date.
export const getAccreditationWiloutExport = async (configToken, status = 'all', since = null) => {
  const params = { status };
  if (since) params.since = new Date(since).toISOString();
  const res = await api.get('/accreditation-config/export-wilout', { ...configTokenHeaders(configToken), params });
  return res.data;
};

// Convertisseur de fichiers dans l'espace de config externe : mêmes signatures que les fonctions
// internes (getAccreditationImports…) pour être injecté tel quel dans AccreditationConverterTool.
// Le `context` passé par l'outil est ignoré — l'API cloisonne l'historique et les mappings par
// projet à partir du token (cf. routes/accreditationExternal.js).
export const accreditationConverterApiForConfig = (configToken) => {
  const auth = configTokenHeaders(configToken);
  return {
    getAccreditationImports: async () => (await api.get('/accreditation-config/converter/imports', auth)).data,
    recordAccreditationImports: async (context, sourceFile, items) =>
      (await api.post('/accreditation-config/converter/imports', { sourceFile, items }, auth)).data,
    deleteAccreditationImports: async (context, { all = false, identityKeys = [] } = {}) =>
      (await api.delete('/accreditation-config/converter/imports', {
        ...auth, params: { all: all ? 'true' : undefined }, data: { identityKeys },
      })).data,
    getAccreditationMapping: async (signature) =>
      (await api.get('/accreditation-config/converter/mappings', { ...auth, params: { signature }, _silent: true })).data?.config || null,
    saveAccreditationMapping: async (signature, config) =>
      (await api.post('/accreditation-config/converter/mappings', { signature, config }, { ...auth, _silent: true })).data,
    fetchWeezeventExport: async (url) =>
      (await api.get('/accreditation-config/converter/weezevent-export', { ...auth, params: { url } })).data,
    claimAccreditationBarcodes: async (context, count, length) =>
      (await api.post('/accreditation-config/converter/barcodes', { count, length }, auth)).data.codes,
    getAccreditationSpares: async (context, filters = {}) =>
      (await api.get('/accreditation-config/converter/spares', { ...auth, params: filters })).data.items,
    getAccreditationSpareBatches: async () =>
      (await api.get('/accreditation-config/converter/spares/batches', auth)).data.batches,
    generateAccreditationSpares: async (context, items, length, pin) =>
      (await api.post('/accreditation-config/converter/spares', { items, length, pin }, auth)).data.items,
    deleteAccreditationSpares: async (context, pin) =>
      (await api.delete('/accreditation-config/converter/spares', { ...auth, data: { pin } })).data.deleted,
    unlockAccreditationSpares: async (context, pin) =>
      api.post('/accreditation-config/converter/spares/unlock', { pin }, { ...auth, _silent: true }),
    setAccreditationSpareUsed: async (context, id, used) =>
      (await api.patch(`/accreditation-config/converter/spares/${id}`, { used }, auth)).data,
    getAccreditationSpareSettings: async () =>
      (await api.get('/accreditation-config/converter/spares/settings', { ...auth, _silent: true })).data.settings,
    saveAccreditationSpareSettings: async (context, settings) =>
      (await api.put('/accreditation-config/converter/spares/settings', { settings }, { ...auth, _silent: true })).data.settings,
  };
};

export const updateAccreditationResponseStatus = async (configToken, formId, responseId, status) => {
  const res = await api.patch(
    `/accreditation-config/forms/${formId}/responses/${responseId}`,
    { status },
    configTokenHeaders(configToken)
  );
  return res.data;
};

export const bulkUpdateAccreditationResponseStatus = async (configToken, formId, responseIds, status) => {
  const res = await api.patch(
    `/accreditation-config/forms/${formId}/responses/bulk`,
    { responseIds, status },
    configTokenHeaders(configToken)
  );
  return res.data;
};

// ===== FORMULAIRE PUBLIC (répondant final — aucune authentification) =====

export const getAccreditationPublicForm = async (token) => {
  const res = await api.get(`/accreditation-form/${token}`, { _silent: true });
  return res.data;
};

// Chef de pôle : règles cashless de son formulaire, par type d'accès { [clé du type]: { credit, units } }.
// Refusé (404) tant que le contact de config n'a pas activé le réglage cashless sur ce formulaire.
export const saveAccreditationTypeRules = async (token, typeRules) => {
  const res = await api.put(`/accreditation-form/${token}/type-rules`, { typeRules });
  return res.data;
};

export const submitAccreditationPublicForm = async (token, data) => {
  const res = await api.post(`/accreditation-form/${token}`, data);
  return res.data;
};

// Lien de suivi personnel (une personne, jamais l'admin) — reçu à la soumission, permet de
// vérifier le statut de SA seule demande sans repasser par l'organisateur.
export const getAccreditationTrackingStatus = async (token) => {
  const res = await api.get(`/accreditation-status/${token}`, { _silent: true });
  return res.data;
};

// ===== LOGISTIQUE STOCK =====

// `team` (ex. 'france') restreint les besoins aux events de cette équipe (+ events sans équipe).
// `country` (ex. 'MA') restreint les besoins aux events des équipes de ce pays (+ events sans
// équipe pour le pays par défaut).
export const getStockOverview = async (country) => {
  const res = await api.get('/logistique/stock/overview', { params: country ? { country } : {} });
  return res.data;
};

export const getDailyAvailability = async (from, to) => {
  const params = {};
  if (from) params.from = from;
  if (to) params.to = to;
  const res = await api.get('/logistique/stock/daily-availability', { params });
  return res.data;
};

// ===== TIMELINE STOCK STATE =====

// Catalogue des articles de stock (libellés, suivi quantité/unité, retour au stock…).
export const getStockCatalog = async () => {
  const res = await api.get('/logistique/stock/catalog');
  return res.data;
};

export const createStockCatalogItem = async (payload) => {
  const res = await api.post('/logistique/stock/catalog', payload);
  return res.data;
};

// Champs d'events observés (suggestions pour les règles de besoin des articles).
export const getStockCatalogEventFields = async () => {
  const res = await api.get('/logistique/stock/catalog/event-fields');
  return res.data;
};

export const updateStockCatalogItem = async (key, payload) => {
  const res = await api.put(`/logistique/stock/catalog/${encodeURIComponent(key)}`, payload);
  return res.data;
};

// Réglages stock : délai de retour, fenêtre d'alerte, pays par défaut, campagne SIM.
export const getStockSettings = async () => {
  const res = await api.get('/logistique/stock/settings');
  return res.data;
};

export const updateStockSettings = async (payload) => {
  const res = await api.put('/logistique/stock/settings', payload);
  return res.data;
};

// Pays de stock = pays des équipes actives : [{ code, label, teams, terminalPrefixes, isDefault }].
export const getStockCountries = async () => {
  const res = await api.get('/logistique/stock/countries');
  return res.data;
};

// Pays de rattachement des terminaux : exceptions posées par un admin au pays déduit du
// préfixe de référence (la référence, écrite sur le boîtier, ne change jamais).
export const getTerminalCountries = async () => {
  const res = await api.get('/terminal-countries');
  return res.data?.items || [];
};

/** country = code pays (ex. 'MA'), ou null pour revenir au pays du préfixe. Admins globaux. */
export const setTerminalCountries = async (deviceIds, country) => {
  const res = await api.put('/terminal-countries', { deviceIds, country: country || null });
  return res.data;
};

// `country` (code pays, ex. 'FR', 'MA') : stock de consommables par pays (FR par défaut côté backend).
export const getStockState = async (date, country) => {
  const params = date ? { date } : {};
  if (country) params.country = country;
  const res = await api.get('/logistique/stock/state', { params });
  return res.data;
};

export const getStockStateEvents = async (itemType, date, windowDays = 60, country) => {
  const params = { windowDays };
  if (date) params.date = date;
  if (country) params.country = country;
  const res = await api.get(`/logistique/stock/state/${itemType}/events`, { params });
  return res.data;
};

export const getStockAlerts = async (from, to, country) => {
  const res = await api.get('/logistique/stock/alerts', { params: { from, to, ...(country ? { country } : {}) } });
  return res.data;
};

// ===== UTILISATEURS AUTHENTIFIES (pour mentions) =====

export const getAuthUsers = async () => {
  const res = await api.get('/auth/users');
  return res.data;
};

// ===== FONCTIONS USERS CRUD =====

export const getUsers = async () => {
  const res = await api.get('/users');
  return res.data;
};

export const createUser = async (userData) => {
  const res = await api.post('/users', userData);
  return res.data;
};

export const updateUser = async (id, userData) => {
  const res = await api.put(`/users/${id}`, userData);
  return res.data;
};

export const deleteUser = async (id) => {
  const res = await api.delete(`/users/${id}`);
  return res.data;
};

export default api;

// ===== FONCTIONS ROLES CRUD =====

export const getRoles = async () => {
  const res = await api.get('/roles');
  return res.data;
};

export const getRoleById = async (id) => {
  const res = await api.get(`/roles/${id}`);
  return res.data;
};

export const createRole = async (roleData) => {
  const res = await api.post('/roles', roleData);
  return res.data;
};

export const updateRole = async (id, roleData) => {
  const res = await api.put(`/roles/${id}`, roleData);
  return res.data;
};

export const deleteRole = async (id) => {
  const res = await api.delete(`/roles/${id}`);
  return res.data;
};

export const seedRoles = async () => {
  const res = await api.post('/roles/seed');
  return res.data;
};

// ===== ASSIGNATION CHEF DE PROJET =====

// Disponibilité jour par jour (events assignés, astreintes, planning mensuel) des personnes
// `emails` entre `start` et `end` (YYYY-MM-DD) — page admin « Assignation ».
export const getStaffAvailability = async ({ start, end, emails }) => {
  const res = await api.get('/planning/availability', { params: { start, end, emails: emails.join(',') } });
  return res.data;
};

export const assignChefToEvent = async (eventId, email, { idEventOnline, idEventPay } = {}) => {
  const res = await api.patch(`/events/${eventId}/assign-chef`, { email, idEventOnline, idEventPay });
  return res.data;
};

// ===== CASHLESS STATS =====

export const patchCashlessStats = (eventId, cashlessStats) =>
  api.patch(`/events/${eventId}/cashless-stats`, { cashlessStats }).then(r => r.data);

// ===== FONCTIONS SELLSY =====

// Récupérer un contact Sellsy par ID
export const getSellsyContact = async (contactId, contactType = null) => {
  const params = contactType ? { type: contactType } : {};
  const res = await api.get(`/sellsy/contacts/${contactId}`, { params });
  return res.data;
};

// Rechercher des contacts Sellsy
export const searchSellsyContacts = async (searchTerm, limit = 10) => {
  const res = await api.get('/sellsy/contacts/search', { 
    params: { searchTerm, limit } 
  });
  return res.data;
};

// Récupérer les documents d'un contact Sellsy
export const getSellsyContactDocuments = async (contactId) => {
  const res = await api.get(`/sellsy/contacts/${contactId}/documents`);
  return res.data;
};

// Tester la connexion à l'API Sellsy
export const testSellsyConnection = async () => {
  const res = await api.get('/sellsy/test');
  return res.data;
};

// Authentification Sellsy
export const getSellsyAuthUrl = async () => {
  const res = await api.get('/sellsy/auth/url');
  return res.data;
};

export const exchangeSellsyCode = async (code) => {
  const res = await api.post('/sellsy/auth/callback', { code });
  return res.data;
};

export const getSellsyAuthStatus = async () => {
  const res = await api.get('/sellsy/auth/status');
  return res.data;
};

export const logoutSellsy = async () => {
  const res = await api.post('/sellsy/auth/logout');
  return res.data;
};

// ============================================================================
// LIAISON SELLSY - ÉVÉNEMENTS
// ============================================================================

/**
 * Lier un client Sellsy à un événement
 * @param {string} eventId - ID de l'événement
 * @param {number} sellsyContactId - ID du contact Sellsy
 */
export const linkSellsyToEvent = async (eventId, sellsyContactId, sellsyType = null) => {
  const res = await api.post(`/events/${eventId}/sellsy/link`, {
    sellsy_contact_id: sellsyContactId,
    sellsy_type: sellsyType
  });
  return res.data;
};

/**
 * Récupérer les informations du client Sellsy lié à un événement
 * @param {string} eventId - ID de l'événement
 */
export const getEventSellsyContact = async (eventId) => {
  const res = await api.get(`/events/${eventId}/sellsy`);
  return res.data;
};

/**
 * Récupérer les documents Sellsy d'un événement
 * @param {string} eventId - ID de l'événement
 */
export const getEventSellsyDocuments = async (eventId) => {
  const res = await api.get(`/events/${eventId}/sellsy/documents`);
  return res.data;
};

/**
 * Récupérer l'historique Sellsy d'un contact
 * @param {string} contactId - ID du contact Sellsy
 * @param {string} contactType - Type du contact (contact, company, individual)
 */
export const getSellsyHistory = async (contactId, contactType = null) => {
  const params = contactType ? { type: contactType } : {};
  const res = await api.get(`/sellsy/contacts/${contactId}/history`, { params });
  return res.data;
};

export const getSellsyDocumentPdf = async (documentId, type) => {
  const res = await api.get(`/sellsy/documents/${documentId}/pdf`, {
    params: { type },
  });
  return res.data;
};

/**
 * Récupérer le PDF d'un document Sellsy lié à un événement
 * @param {string} eventId - ID de l'événement
 * @param {string} documentId - ID du document Sellsy
 * @param {string} type - Type du document (invoice, estimate, order)
 */
export const getEventSellsyDocumentPdf = async (eventId, documentId, type) => {
  const res = await api.get(`/events/${eventId}/sellsy/documents/${documentId}/pdf`, {
    params: { type },
  });
  return res.data;
};

/**
 * Délier un client Sellsy d'un événement
 * @param {string} eventId - ID de l'événement
 */
export const unlinkSellsyFromEvent = async (eventId) => {
  const res = await api.delete(`/events/${eventId}/sellsy/link`);
  return res.data;
};

/**
 * Retirer un client Sellsy spécifique d'un événement
 * @param {string} eventId - ID de l'événement
 * @param {number|string} sellsyContactId - ID Sellsy à retirer
 * @param {string} sellsyType - Type Sellsy (contact, company, individual)
 */
export const unlinkSellsyContactFromEvent = async (eventId, sellsyContactId, sellsyType = null) => {
  const params = sellsyType ? { type: sellsyType } : {};
  const res = await api.delete(`/events/${eventId}/sellsy/link/${sellsyContactId}`, { params });
  return res.data;
};

/**
 * Mettre à jour le lien client Sellsy (changer de client)
 * @param {string} eventId - ID de l'événement
 * @param {number} sellsyContactId - ID du nouveau contact Sellsy
 */
export const updateEventSellsyLink = async (eventId, sellsyContactId, sellsyType = null) => {
  const res = await api.put(`/events/${eventId}/sellsy/link`, {
    sellsy_contact_id: sellsyContactId,
    sellsy_type: sellsyType
  });
  return res.data;
};

// ===== FONCTIONS QR CODES DYNAMIQUES =====

export const getDynamicQRs = async () => {
  const res = await api.get('/qrcodes');
  return res.data;
};

export const updateDynamicQR = async (id, data) => {
  const res = await api.put(`/qrcodes/${id}`, data);
  return res.data;
};

export const deleteDynamicQR = async (id) => {
  const res = await api.delete(`/qrcodes/${id}`);
  return res.data;
};

// Aperçu (lecture seule) d'un import de shortags : rows = [[shortTag, uid], ...]
export const previewEventShortags = async (eventId, rows) => {
  const res = await api.post(`/events/${eventId}/shortag-preview`, { rows });
  return res.data;
};

// ===== PLANNING =====

export const getMyPlanning = async (start, end) => {
  const params = {};
  if (start) params.start = start;
  if (end) params.end = end;
  const res = await api.get('/planning/me', { params });
  return res.data;
};

export const getAdminPlanning = async (start, end) => {
  const params = {};
  if (start) params.start = start;
  if (end) params.end = end;
  const res = await api.get('/planning/all', { params });
  return res.data;
};

// ===== PLANNING MENSUEL (MONTHLY SCHEDULES) =====

export const getMySchedule = async (month) => {
  const res = await api.get('/monthly-schedules/me', { params: { month } });
  return res.data;
};

export const getAllSchedules = async (month) => {
  const res = await api.get('/monthly-schedules/all', { params: { month } });
  return res.data;
};

export const getUserSchedule = async (email, month) => {
  const res = await api.get(`/monthly-schedules/user/${email}`, { params: { month } });
  return res.data;
};

export const createSchedule = async (data) => {
  const res = await api.post('/monthly-schedules', data);
  return res.data;
};

export const updateSchedule = async (id, data) => {
  const res = await api.put(`/monthly-schedules/${id}`, data);
  return res.data;
};

export const submitSchedule = async (id) => {
  const res = await api.put(`/monthly-schedules/${id}/submit`);
  return res.data;
};

export const validateSchedule = async (id) => {
  const res = await api.put(`/monthly-schedules/${id}/validate`);
  return res.data;
};

export const unvalidateSchedule = async (id) => {
  const res = await api.put(`/monthly-schedules/${id}/unvalidate`);
  return res.data;
};

export const deleteSchedule = async (id) => {
  const res = await api.delete(`/monthly-schedules/${id}`);
  return res.data;
};

export const generateSchedulePreview = async ({ userEmail, month, defaultStart, defaultEnd, defaultBreakMinutes }) => {
  const res = await api.post('/monthly-schedules/generate-preview', { userEmail, month, defaultStart, defaultEnd, defaultBreakMinutes });
  return res.data;
};

export const importSchedulePdfPreview = async (file, month) => {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('month', month);
  const res = await api.post('/monthly-schedules/import-pdf-preview', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
};

// ===== STOCK CONSOMMABLES =====

export const getStockItems = async (country) => {
  const res = await api.get('/logistique/stock/items', { params: country ? { country } : {} });
  return res.data;
};

export const updateStockItem = async (id, data) => {
  const res = await api.put(`/logistique/stock/items/${id}`, data);
  return res.data;
};

export const getStockMovements = async (params = {}) => {
  const res = await api.get('/logistique/stock/movements', { params });
  return res.data;
};

// ===== UNIFIED STOCK MOVEMENTS =====

export const createStockExit = async (data) => {
  const res = await api.post('/logistique/stock/movements/exit', data);
  return res.data;
};

export const createStockReturn = async (data) => {
  const res = await api.post('/logistique/stock/movements/return', data);
  return res.data;
};

export const getEventStockMovements = async (eventId) => {
  const res = await api.get(`/logistique/stock/movements/event/${eventId}`);
  return res.data;
};

export const getEventStockSummary = async (eventId) => {
  const res = await api.get(`/logistique/stock/movements/event/${eventId}/summary`);
  return res.data;
};

export const getStockCirculationV2 = async () => {
  const res = await api.get('/logistique/stock/movements/circulation');
  return res.data;
};

// ===== ANTICIPATION CDP =====

export const getCdpForecast = async (start, end) => {
  const res = await api.get('/anticipation/cdp-forecast', { params: { start, end } });
  return res.data;
};

export const upsertOverride = async (data) => {
  const res = await api.put('/anticipation/override', data);
  return res.data;
};

export const deleteOverride = async (id) => {
  const res = await api.delete(`/anticipation/override/${id}`);
  return res.data;
};

// ===== ASTREINTES =====

export const getAstreintes = async () => {
  const res = await api.get('/astreintes');
  return res.data;
};

export const createAstreinte = async (data) => {
  const res = await api.post('/astreintes', data);
  return res.data;
};

export const updateAstreinte = async (id, data) => {
  const res = await api.put(`/astreintes/${id}`, data);
  return res.data;
};

export const deleteAstreinte = async (id) => {
  const res = await api.delete(`/astreintes/${id}`);
  return res.data;
};

// ===== ASTREINTE INTERVENTIONS =====

export const getAstreinteInterventions = async (params = {}) => {
  const res = await api.get('/astreintes/interventions', { params });
  return res.data;
};

export const getMyAstreinteInterventions = async (params = {}) => {
  const res = await api.get('/astreintes/interventions/me', { params });
  return res.data;
};

export const getEventAstreinteInterventions = async (eventId) => {
  const res = await api.get(`/astreintes/interventions/event/${eventId}`);
  return res.data;
};

export const getAstreinteInterventionsStats = async (params = {}) => {
  const res = await api.get('/astreintes/interventions/stats', { params });
  return res.data;
};

export const createAstreinteIntervention = async (data) => {
  const res = await api.post('/astreintes/interventions', data);
  return res.data;
};

export const updateAstreinteIntervention = async (id, data) => {
  const res = await api.put(`/astreintes/interventions/${id}`, data);
  return res.data;
};

export const deleteAstreinteIntervention = async (id) => {
  const res = await api.delete(`/astreintes/interventions/${id}`);
  return res.data;
};

// ===== TACHES =====

export const getTasks = async (params = {}) => {
  const res = await api.get('/tasks', { params });
  return res.data;
};

export const getMyTasks = async (params = {}) => {
  const res = await api.get('/tasks/me', { params });
  return res.data;
};

export const getPoolTasks = async () => {
  const res = await api.get('/tasks/pool');
  return res.data;
};

export const claimTask = async (id) => {
  const res = await api.patch(`/tasks/${id}/claim`);
  return res.data;
};

export const getMyTaskCounts = async () => {
  const res = await api.get('/tasks/me/counts');
  return res.data;
};

export const getTaskById = async (id) => {
  const res = await api.get(`/tasks/${id}`);
  return res.data;
};

export const createTask = async (data) => {
  const res = await api.post('/tasks', data);
  return res.data;
};

export const updateTask = async (id, data) => {
  const res = await api.put(`/tasks/${id}`, data);
  return res.data;
};

export const updateTaskStatus = async (id, statut) => {
  const res = await api.patch(`/tasks/${id}/status`, { statut });
  return res.data;
};

// Définit l'ensemble des assignés. `assignees` = tableau [{ email, nom }].
// Rétro-compat : accepte aussi assignTask(id, email, nom).
export const assignTask = async (id, assignees, nom) => {
  const body = Array.isArray(assignees)
    ? { assignes: assignees }
    : { email: assignees, nom };
  const res = await api.patch(`/tasks/${id}/assign`, body);
  return res.data;
};

export const unassignTask = async (id) => {
  const res = await api.patch(`/tasks/${id}/unassign`);
  return res.data;
};

export const deleteTask = async (id) => {
  const res = await api.delete(`/tasks/${id}`);
  return res.data;
};

// ─── Teams ────────────────────────────────────────────────
export const getTeams = async () => {
  const res = await api.get('/teams');
  return res.data;
};

export const getTeamBySlug = async (slug) => {
  const res = await api.get(`/teams/${slug}`);
  return res.data;
};

export const createTeam = async (data) => {
  const res = await api.post('/teams', data);
  return res.data;
};

export const updateTeam = async (slug, data) => {
  const res = await api.put(`/teams/${slug}`, data);
  return res.data;
};

export const deleteTeam = async (slug) => {
  const res = await api.delete(`/teams/${slug}`);
  return res.data;
};

// ===== JOURNAL D'ACTIVITE (LOGS) =====

export const getLogs = (params) => api.get('/logs', { params }).then(r => r.data);
export const getLogStats = () => api.get('/logs/stats').then(r => r.data);

// ===== RENDEZ-VOUS GOOGLE MEET (FICHE EVENT) =====

export const createEventRendezVous = async (eventId, data) => {
  const res = await api.post(`/events/${eventId}/rendez-vous`, data);
  return res.data;
};

export const deleteEventRendezVous = async (eventId, rdvId) => {
  const res = await api.delete(`/events/${eventId}/rendez-vous/${rdvId}`);
  return res.data;
};

export const saveEventRendezVousRecap = async (eventId, rdvId, recap) => {
  const res = await api.post(`/events/${eventId}/rendez-vous/${rdvId}/recap`, { recap });
  return res.data;
};

// Connexion Google de l'utilisateur courant (agenda + Meet).
// Route distincte du module prospection : accessible a tout utilisateur authentifie.
export const getGoogleConnection = async () => {
  const res = await api.get('/google-account/connection');
  return res.data;
};

export const getGoogleAuthUrl = async (returnPath) => {
  const res = await api.get('/google-account/auth-url', { params: { returnPath } });
  return res.data;
};

// ===== NOTIFICATIONS PUSH (envoi manuel admin) =====

export const getRegisteredPushUsers = async () => {
  const res = await api.get('/push-tokens/registered');
  return res.data.users || [];
};

export const sendPushNotification = async ({ emails, title, body }) => {
  const res = await api.post('/push-tokens/send', { emails, title, body });
  return res.data;
};
